import { Box, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useDashboardContext } from '../../contexts/DashboardContextProvider'

export function IncomePlot() {
  const { fetching, purchasedRfqs, recentPayments } = useDashboardContext()

  type PlotDatum = {
    month: string
    vendors: Record<string, number>
  }
  const chartData = useMemo(() => {
    if (purchasedRfqs && recentPayments) {
      const d = recentPayments.reduce((acc: PlotDatum[], payment) => {
        const month = dayjs(payment.dateReceived || payment.dateCreated).format('MMM YYYY')
        const existing = acc.find((d) => d.month === month)
        if (existing) {
          existing.vendors[payment.vendor.name] = (existing.vendors[payment.vendor.name] || 0) + payment.amount
        } else {
          const newDatum = { month, vendors: { [payment.vendor.name]: payment.amount } }
          acc.push(newDatum)
        }
        return acc
      }, [] as PlotDatum[])

      const outstanding: PlotDatum = {
        month: 'Outstanding',
        vendors: {},
      }
      for (const rfq of purchasedRfqs) {
        const vendor = rfq.vendor.name
        const po = rfq.quote?.purchaseOrder
        if (po) {
          const comm = po.amount * (po.commissionPercentage / 100)
          outstanding.vendors[vendor] = (outstanding.vendors[vendor] || 0) + comm
        }
      }
      if (Object.keys(outstanding.vendors).length > 0) {
        d.push(outstanding)
      }
      return d
    }
    return []
  }, [purchasedRfqs, recentPayments])

  const vendors = useMemo(() => {
    if (purchasedRfqs && recentPayments) {
      const s = new Set(recentPayments.map((p) => p.vendor.name))
      purchasedRfqs.forEach((rfq) => s.add(rfq.vendor.name))
      return Array.from(s)
    }
    return []
  }, [purchasedRfqs, recentPayments])

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#d8c4b3', '#82ca9d']

  const tickCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  if (chartData.length < 2) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={2}>
        <Typography>Not enough income to chart</Typography>
        <Typography variant="caption">
          Once you've entered more payments we can chart your income and outstanding estimated commission.
        </Typography>
      </Stack>
    )
  }

  if (fetching) return <Box>Loading...</Box>
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} width={500} height={300}>
        <XAxis dataKey="month" type="category" />
        <YAxis tickFormatter={tickCurrencyFormatter.format} />
        <Tooltip formatter={(v: number) => tickCurrencyFormatter.format(v)} />
        <Legend />
        {vendors.map((vendor, i) => (
          <Bar key={vendor} dataKey={`vendors.${vendor}`} stackId="a" fill={colors[i % colors.length]} name={vendor} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
